import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getFlipX } from "../../ftAdjustments"
import { createLowRowClip } from "../ftSharedParts"
import { getRackModel, ATTACHMENTS, CTM1_ACCESSORIES } from "@/js/pages/product/rackbuilder/modelPaths"
import { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"

// Slinger X Parts — part definitions for the standalone rack and the Cave 2.0 assembly

// MARK: Context
export interface SlingerXContext {
  config: RackOptions
  materials: MaterialInfo
  isRML: boolean // RML/FML line vs RM/FM line — drives slot names + per-line mesh-calibration offsets
  slingerLine: string // Blender file prefix for line-specific parts ('FML' vs 'FM')
}

// MARK: Weight Stack
export function buildWeightStack(context: SlingerXContext, left: boolean): PartDefinition[] {
  const { config } = context
  const stack: PartDefinition[] = [buildWeights(context, left)]

  if (config.ftShrouds) {
    stack.push(buildShrouds(context, left))
  }

  return stack
}

function buildWeightStackPoles(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, materials, isRML } = context

  // 100" poles mesh is shared between RM/RML; other heights have separate line-specific meshes
  const meshName = config.height === '100'
    ? `poles ${config.height}`
    : `${isRML ? 'rml' : 'rm'} poles ${config.height}`

  return {
    mesh        : getRackModel('SLINGER_X_SHARED', meshName),
    slots       : {},
    anchor      : 'top',
    offset      : isRML ? { x: -0.2, z: -0.1 } : {},
    materials,
    adjustments : getFlipX(left),
  }
}

// Top plate/hardware — a different mesh from the INDY's.
function buildWeightStackTop(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, isRML } = context
  const yOffset = config.weightStackAmount === '300' ? -0.9 : -0.8

  return {
    mesh  : getRackModel('SLINGER_X_SHARED', 'weight stack top'),
    slots : {
      // RML routes its bottom weight cable elsewhere, so none is nested here.
      weight_cable: isRML ? [] : buildBottomWeightStackCable(context, left),
    },
    offset: { y: yOffset },
  }
}

function buildWeightStack300Plates(): PartDefinition {
  return {
    mesh  : getRackModel('SLINGER_X_SHARED', 'weight stack plates 300'),
    slots : {},
  }
}

function buildWeightStackBottom(context: SlingerXContext): PartDefinition {
  const { isRML, slingerLine } = context

  return {
    mesh   : getRackModel(`${slingerLine}_SLINGER_X`, `${slingerLine} weight stack bottom`),
    slots  : {},
    offset : isRML ? { z: 2 } : {},
  }
}

function buildWeights(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, materials, isRML } = context
  const zOffset = isRML ? 9 : 0.1

  const weights: PartDefinition = {
    mesh  : getRackModel('SLINGER_X_SHARED', 'weight stack'),
    slots : {
      weight_stack_bottom: buildWeightStackBottom(context),
    },
    materials,
    offset      : { z: zOffset },
    adjustments : getFlipX(left),
  }

  if (config.weightStackAmount === '300') {
    weights.slots.weights_300 = [buildWeightStackTop(context, left), buildWeightStack300Plates()]
  } else {
    weights.slots.weights_250 = buildWeightStackTop(context, left)
  }

  return weights
}

function buildShrouds(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, materials, isRML, slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, `shroud ${config.height}`),
    slots       : {},
    anchor      : 'bottom',
    materials,
    offset      : isRML ? {} : { z: 0.1 },
    adjustments : left ? [] : [{ axis: 'Z', type: 'rotate', amount: 180, pieces: ['clasp'] }],
  }
}

// MARK: Cables
function buildFrontHRCable(context: SlingerXContext, left: boolean): PartDefinition {
  const { slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, 'front cable hr'),
    slots       : {},
    adjustments : getFlipX(left),
  }
}

function buildFrontCable(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, `six post ${config.depth}`),
    slots       : {},
    adjustments : getFlipX(left),
  }
}

function buildBottomWeightStackCable(context: SlingerXContext, left: boolean): PartDefinition {
  const { slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, 'bottom weight cable'),
    slots       : {},
    adjustments : getFlipX(left),
  }
}

function buildTopWeightStackCable(context: SlingerXContext, left: boolean): PartDefinition {
  const { isRML, slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, `${isRML ? 'bottom' : 'top'} weight cable`),
    slots       : {},
    offset      : isRML && left ? { x: -0.2 } : {},
    adjustments : getFlipX(left),
  }
}

function buildBottomExtensionCable(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, slingerLine } = context
  const meshName = `${config.height} no foot extension cable`

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, meshName),
    slots       : {},
    anchor      : 'bottom',
    adjustments : getFlipX(left),
  }
}

// MARK: Seat
// RM's seat mesh has no separate low row slot - the clip sits directly on the seat itself, same as before the kit split.
function buildRMSeat(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, 'lat pulldown seat'),
    slots       : { clip: createLowRowClip(config.lowRowSelection) },
    offset      : { y: -33 },
    adjustments : getFlipX(left),
  }
}

// RML has 2 seat products - with and without the low row kit; the kit is a separate mesh inserted into the
// seat's "low" slot, and the low row attachment clip belongs to the kit itself - no kit, no clip to attach to.
function buildRMLSeat(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, slingerLine } = context

  const lowRowKit: PartDefinition = {
    mesh  : getRackModel(`${slingerLine}_SLINGER_X`, 'low row'),
    slots : { clip: createLowRowClip(config.lowRowSelection) },
  }

  return {
    mesh  : getRackModel(`${slingerLine}_SLINGER_X`, 'lat pulldown seat'),
    slots : {
      low: config.seatLowRowKit ? [lowRowKit] : [],
    },
    offset      : { y: -27 },
    adjustments : getFlipX(left),
  }
}

export function buildSeat(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, isRML } = context

  // "Rogue Monster Multi-Use Lat Seat" — the CTM-1 seat, reused here from CTM1_ACCESSORIES (mirrors ctm1Builder.ts's own seat).
  if (config.seatSelection?.toLowerCase().includes('multi-use')) {
    return {
      mesh        : `${CTM1_ACCESSORIES}#seat`,
      slots       : {},
      offset      : { y: -16, z: -0.4 },
      adjustments : getFlipX(left),
    }
  }

  return isRML ? buildRMLSeat(context, left) : buildRMSeat(context, left)
}

// MARK: Pulleys & Hardware
function buildTopCarabiner(context: SlingerXContext, left: boolean): PartDefinition {
  const { slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, 'carabiner'),
    slots       : {},
    adjustments : getFlipX(left),
  }
}

export function buildSwivelHanger(context: SlingerXContext, left: boolean): PartDefinition {
  const { config, slingerLine, isRML } = context
  const yOffset = !isRML ? (config.height === '100' ? 10 : 0) : 0

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, 'swivel hanger'),
    slots       : {},
    offset      : { y: yOffset },
    adjustments : getFlipX(!left),
  }
}

// Slinger-X `low row clip` mesh with the selected attachment nested — distinct from the shared createLowRowClip used by buildSeat.
function buildLowRowClip(context: SlingerXContext): PartDefinition {
  const { config, slingerLine } = context
  const lowRowAttachment: PartDefinition = {
    mesh  : `${ATTACHMENTS}#${config.lowRowSelection}`,
    slots : {},
  }

  return {
    mesh  : getRackModel(`${slingerLine}_SLINGER_X`, 'low row clip'),
    slots : { bottom: config.lowRowSelection ? [lowRowAttachment] : [] },
  }
}

// MARK: Bottom Swivel Pulley
export function buildBottomSwivelPulley(context: SlingerXContext, left: boolean): PartDefinition {
  const { slingerLine } = context

  return {
    mesh  : getRackModel(`${slingerLine}_SLINGER_X`, 'bottom swivel pulley'),
    slots : {
      cable : buildBottomExtensionCable(context, left),
      clip  : buildLowRowClip(context),
    },
    anchor      : 'bottom',
    offset      : { y: -8 },
    adjustments : getFlipX(!left),
  }
}

function buildRmFrontCable(context: SlingerXContext, left: boolean, latBarClip: PartDefinition): PartDefinition {
  const { config, slingerLine } = context

  return {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, `${config.height} front cable`),
    slots       : { clip: config.swivelPulley ? buildTopCarabiner(context, left) : latBarClip },
    adjustments : getFlipX(left),
  }
}

// MARK: Front Pulley
export function buildFrontPulley(context: SlingerXContext, left: boolean, latBarClip: PartDefinition): PartDefinition {
  const { config, materials, slingerLine, isRML } = context

  const frontPulley: PartDefinition = {
    mesh        : getRackModel(`${slingerLine}_SLINGER_X`, 'front pulley'),
    slots       : {},
    anchor      : "top",
    materials,
    adjustments : getFlipX(left),
  }

  if (isRML && config.swivelPulley) {
    frontPulley.slots['clip'] = buildTopCarabiner(context, left) 
  } else if (isRML) {
    frontPulley.slots['clip'] = latBarClip
  } else if (!isRML) {
    frontPulley.slots['front_cable'] = buildRmFrontCable(context, left, latBarClip)
  }


  if (config.halfRack) {
    frontPulley.slots.cable = buildFrontHRCable(context, left)
  } else {
    frontPulley.slots.cable = buildFrontCable(context, left)
  }

  return frontPulley
}

export function buildBackPulley(context: SlingerXContext, left: boolean): PartDefinition {
  const { materials, isRML, slingerLine } = context

  return {
    mesh  : getRackModel(`${slingerLine}_SLINGER_X`, 'back pulley'),
    slots : {
      poles        : buildWeightStackPoles(context, left),
      weight_cable : buildTopWeightStackCable(context, left),
    },
    anchor      : "back",
    materials,
    offset      : isRML && left ? { x: -0.3 } : {},
    adjustments : isRML ? getFlipX(!left) : [],
  }
}
